import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { TwilioAddressType, TwilioEndUserType } from 'src/accounts/types';
import { Toast } from 'src/common/components';

import { EMAIL_REGEX, ZIP_CODE_REGEX } from '../../constants';
import {
  createTwilioConfiguration,
  createTwilioStarterProfile,
  createTwilioEndUser,
  createTwilioAddress,
  createTwilioA2PProfile,
  createTwilioA2PBrand,
  createTwilioA2PUseCase,
} from '../api';
import { Action } from '../constants';
import { useAccountProfileContext } from '../context';

const TwilioConfiguration: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const { state, dispatch } = useAccountProfileContext();
  const { account } = state;
  const { twilio_configuration: twilioConfiguration } = account;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TwilioEndUserType>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
    },
  });

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {
    register: addressRegister,
    handleSubmit: addressHandleSubmit,
    formState: { errors: addressErrors },
  } = useForm<TwilioAddressType>({
    defaultValues: {
      street: '',
      city: '',
      zipcode: '',
      state: 'PR',
      country: 'US',
    },
  });

  const handleCreateConfiguration = async (): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioConfiguration(account.id);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateStarterProfile = async (): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioStarterProfile(account.id);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const onSubmitEndUser = async (data: TwilioEndUserType): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioEndUser(account.id, data);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const onSubmitAddress = async (data: TwilioAddressType): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioAddress(account.id, data);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateA2PProfile = async (): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioA2PProfile(account.id);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateA2PBrand = async (): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioA2PBrand(account.id);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateA2PUseCase = async (): Promise<void> => {
    try {
      setIsCreating(true);
      const response = await createTwilioA2PUseCase(account.id);
      dispatch({
        type: Action.UPDATE_TWILIO_CONFIGURATION,
        twilio_configuration: { ...twilioConfiguration, ...response },
      });
      Toast.success('Changes saved successfully.');
    } catch (err) {
      Toast.error('There was an error saving changes.');
    } finally {
      setIsCreating(false);
    }
  };

  const isProcessCompleted: boolean = twilioConfiguration
    ? Object.values(twilioConfiguration).every((sid) => sid)
    : false;

  if (twilioConfiguration) {
    return (
      <div className="flex flex-col px-4 py-5 mt-6">
        <div className="sm:px-6 px-4 py-5 bg-white-1.0 rounded max-w-4xl">
          <div className="sm:px-6 px-4 py-2">
            <h4 data-testid="name-header">Subaccount Information</h4>
          </div>
          <div className="sm:px-6 grid grid-cols-2">
            <div className="py-5">
              <p className="helper-text">Status</p>
              <code data-testid="cached-subaccount-status">
                {twilioConfiguration.cached_subaccount_status}
              </code>
            </div>
            <div className="py-5">
              <p className="helper-text">SID</p>
              <code data-testid="subaccount-account-sid">
                {twilioConfiguration.subaccount_account_sid}
              </code>
            </div>
            <div className="py-5">
              <p className="helper-text">Authorization token</p>
              <code data-testid="subaccount-auth-token">
                {twilioConfiguration.subaccount_auth_token}
              </code>
            </div>
            <div className="py-5">
              <p className="helper-text">Messaging service id</p>
              <code data-testid="messaging-service-sid">
                {twilioConfiguration.messaging_service_sid}
              </code>
            </div>
          </div>
        </div>
        <div className="sm:px-6 px-4 py-10">
          <h2 data-testid="name-header">
            App-To-Person Registration
            {isProcessCompleted && (
              <i className="icon-check_icon bg-secondary-0.0 text-white-0.0 rounded ml-4" />
            )}
          </h2>
        </div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4>Starter business profile id</h4>
            <p className="helper-text">
              Starter business profile created using the account name and email
            </p>
          </div>

          <p className="self-center">
            {twilioConfiguration.starter_profile_sid ? (
              <code data-testid="starter-profile-sid">
                {twilioConfiguration.starter_profile_sid}
              </code>
            ) : (
              <button
                className="btn btn-secondary ml-auto"
                type="button"
                data-testid="create-starter-profile"
                onClick={handleCreateStarterProfile}
                disabled={isCreating}
              >
                {isCreating ? 'Creating Starter Profile ...' : 'Create Starter Profile'}
              </button>
            )}
          </p>
        </div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4 className={`${!twilioConfiguration.starter_profile_sid ? 'text-gray-1.0 ' : ''}`}>
              End User resource id
            </h4>
            <p className="helper-text">
              End User resource created using the owner&apos;s contact information
            </p>
          </div>
          {twilioConfiguration.starter_profile_sid && (
            <div className="self-center">
              {twilioConfiguration.end_user_sid ? (
                <code data-testid="end-user-sid">{twilioConfiguration.end_user_sid}</code>
              ) : (
                <form className="flex flex-col mt-3" onSubmit={handleSubmit(onSubmitEndUser)}>
                  <label htmlFor="first_name" className="input-label py-2">
                    First Name
                    <input
                      id="first_name"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...register('first_name', { required: true })}
                      data-testid="first-name-testid"
                    />
                  </label>
                  {errors.first_name && <p className="error-text"> The first name is required</p>}
                  <label htmlFor="last-name" className="input-label py-2">
                    Last Name
                    <input
                      id="last_name"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...register('last_name', { required: true })}
                      data-testid="last-name-testid"
                    />
                  </label>
                  {errors.last_name && <p className="error-text"> The last name is required</p>}
                  <label htmlFor="email" className="input-label py-2">
                    Email
                    <input
                      id="email"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...register('email', {
                        required: 'The email is required',
                        pattern: {
                          value: EMAIL_REGEX,
                          message: 'Entered value does not match email format',
                        },
                      })}
                      data-testid="email-testid"
                    />
                  </label>
                  {errors.email && <p className="error-text"> {errors.email?.message}</p>}
                  <label htmlFor="phone_number" className="input-label py-2">
                    Phone Number
                    <input
                      id="phone_number"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...register('phone_number', { required: 'Phone number is required' })}
                      data-testid="phone-number-testid"
                    />
                  </label>
                  {errors.phone_number && (
                    <p className="error-text">{errors.phone_number?.message}</p>
                  )}
                  <button
                    className="btn btn-secondary mt-4 ml-auto"
                    type="submit"
                    data-testid="submit-end-user"
                    disabled={isCreating}
                  >
                    {isCreating ? 'Creating End User ...' : 'Create End User'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4 className={`${!twilioConfiguration.end_user_sid ? 'text-gray-1.0 ' : ''}`}>
              Address resource id
            </h4>
            <p className="helper-text">
              Address resource created using the client location information
            </p>
          </div>
          {twilioConfiguration.end_user_sid && (
            <div>
              {twilioConfiguration.address_sid ? (
                <code data-testid="address-sid">{twilioConfiguration.address_sid}</code>
              ) : (
                <form
                  className="sm:grid sm:grid-cols-2 sm:gap-8"
                  onSubmit={addressHandleSubmit(onSubmitAddress)}
                >
                  <label htmlFor="street" className="input-label sm:col-span-2">
                    Street
                    <input
                      id="street"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...addressRegister('street', { required: true })}
                      data-testid="street-testid"
                    />
                    {addressErrors.street && (
                      <p className="error-text"> The street address is required</p>
                    )}
                  </label>
                  <label htmlFor="city" className="input-label">
                    City
                    <input
                      id="city"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...addressRegister('city', { required: true })}
                      data-testid="city-testid"
                    />
                    {addressErrors.city && <p className="error-text"> The city name is required</p>}
                  </label>
                  <label htmlFor="zipcode" className="input-label">
                    Zip Code
                    <input
                      id="zipcode"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...addressRegister('zipcode', {
                        required: 'The zip-code is required',
                        pattern: {
                          value: ZIP_CODE_REGEX,
                          message: 'Entered value does not match zip-code format',
                        },
                      })}
                      data-testid="zipcode-testid"
                    />
                    {addressErrors.zipcode && (
                      <p className="error-text"> {addressErrors.zipcode?.message}</p>
                    )}
                  </label>
                  <label htmlFor="state" className="input-label">
                    State
                    <input
                      id="state"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...addressRegister('state', { required: true })}
                      data-testid="state-testid"
                    />
                    {addressErrors.state && (
                      <p className="error-text"> The state name is required</p>
                    )}
                  </label>
                  <label htmlFor="country" className="input-label">
                    Country
                    <input
                      id="country"
                      className="input sm:mt-0 w-full mt-2 transition duration-150 ease-in-out"
                      type="text"
                      {...addressRegister('country', { required: true })}
                      data-testid="country-testid"
                    />
                    {addressErrors.country && (
                      <p className="error-text"> The country name is required</p>
                    )}
                  </label>
                  <button
                    className="btn btn-secondary col-start-2"
                    type="submit"
                    data-testid="submit-address"
                    disabled={isCreating}
                  >
                    {isCreating ? 'Creating Address ...' : 'Create Address'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4 className={`${!twilioConfiguration.address_sid ? 'text-gray-1.0 ' : ''}`}>
              App-To-Person profile id
            </h4>
            <p className="helper-text">
              App-To-Person profile created using the account name and email
            </p>
          </div>
          {twilioConfiguration.address_sid && (
            <p className="self-center">
              {twilioConfiguration.a2p_profile_sid ? (
                <code data-testid="a2p-profile-sid">{twilioConfiguration.a2p_profile_sid}</code>
              ) : (
                <button
                  className="btn btn-secondary ml-auto"
                  type="button"
                  data-testid="create-a2p-profile"
                  onClick={handleCreateA2PProfile}
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating A2P Profile ...' : 'Create A2P Profile'}
                </button>
              )}
            </p>
          )}
        </div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4 className={`${!twilioConfiguration.a2p_profile_sid ? 'text-gray-1.0 ' : ''}`}>
              App-To-Person brand id
            </h4>
            <p className="helper-text">
              App-To-Person brand created using the starter profile and the a2p profile
            </p>
          </div>
          {twilioConfiguration.a2p_profile_sid && (
            <p className="self-center">
              {twilioConfiguration.a2p_brand_sid ? (
                <code data-testid="a2p-brand-sid">{twilioConfiguration.a2p_brand_sid}</code>
              ) : (
                <button
                  className="btn btn-secondary ml-auto"
                  type="button"
                  data-testid="create-a2p-brand"
                  onClick={handleCreateA2PBrand}
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating A2P Brand ...' : 'Create A2P Brand'}
                </button>
              )}
            </p>
          )}
        </div>

        <div className="sm:grid sm:grid-cols-2 sm:gap-8 sm:px-6 px-4 py-5 border-b-2 border-white-1.0">
          <div className="sm:col-span-1">
            <h4 className={`${!twilioConfiguration.a2p_brand_sid ? 'text-gray-1.0 ' : ''}`}>
              App-To-Person Use Case id
            </h4>
            <p className="helper-text">
              App-To-Person Use Case created using the a2p brand and sample messages send by the
              account
            </p>
          </div>
          {twilioConfiguration.a2p_brand_sid && (
            <p className="self-center">
              {twilioConfiguration.a2p_use_case_sid ? (
                <code data-testid="a2p-use-case-sid">{twilioConfiguration.a2p_use_case_sid}</code>
              ) : (
                <button
                  className="btn btn-secondary ml-auto"
                  type="button"
                  data-testid="create-a2p-use-case"
                  onClick={handleCreateA2PUseCase}
                  disabled={isCreating}
                >
                  {isCreating ? 'Creating A2P Use Case ...' : 'Create A2P Use Case'}
                </button>
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-6">
      <div className="px-4 py-5">
        <button
          className="btn btn-secondary ml-auto"
          type="button"
          data-testid="create-subaccount"
          onClick={handleCreateConfiguration}
          disabled={isCreating}
        >
          {isCreating ? 'Creating Twilio subaccount ...' : 'Create Twilio subaccount'}
        </button>
      </div>
    </div>
  );
};

export default TwilioConfiguration;
